import { z } from 'zod';
import { STORE_CURRENCIES, STORE_PRODUCT_KINDS, storeProductSnapshotSchema } from './catalog';
import { STORE_ORDER_STATES } from './orders';
import { RECREDIT_EXCLUSION_REASONS, RECREDIT_PREVIEW_STATUS } from './recredit';
import { RELEASE_STAGES, RELEASE_TRANSITIONS } from './release';

/**
 * The store's wire contract.
 *
 * ## What a request may never say
 *
 * No `accountId`: the caller is the authenticated session. No price, no
 * currency, no Cash amount, no VIP duration, no order state, no external
 * reference, no payment id, no notification URL and no recipient. Creating an
 * order carries a `productId` in the body and an idempotency key in a header,
 * and that is the entire input.
 *
 * Every request schema is `.strict()`, so an unknown field is a refusal rather
 * than a silently ignored one - which is what stops `{ priceCents: 1 }` from
 * being harmlessly dropped today and quietly honoured after a refactor.
 *
 * ## What a response never carries
 *
 * No access token, no webhook secret, no signature, no provider payload, no
 * recipient id, no document and nothing about another account. The order view
 * carries the provider's NAME, its environment, the payment instructions the
 * buyer needs, and what a support conversation would actually ask for.
 */

export const STORE_CONTRACT_VERSION = 1 as const;

export const STORE_CATALOG_ROUTE = '/api/store/catalog' as const;
export const STORE_ORDERS_ROUTE = '/api/store/orders' as const;
export const storeOrderRoute = (orderId: string) => `/api/store/orders/${orderId}` as const;

/**
 * What the launch would credit this account, if it happened now.
 *
 * A READ, and only ever a read. There is no companion write: the recredit is
 * paid by the official reset, which does not exist yet, and no route in this
 * API grants, reserves or promises any part of it.
 */
export const STORE_RECREDIT_PREVIEW_ROUTE = '/api/store/recredit-preview' as const;

/**
 * Where Mercado Pago posts.
 *
 * NOT session-protected, and it does not need to be: the signature is the
 * authority, and a body that fails it is never acted on. Blocking by source IP
 * is deliberately absent - providers change infrastructure, and an IP allow-list
 * would eventually reject legitimate notifications while adding nothing a
 * forged signature could not already defeat.
 */
export const STORE_MERCADOPAGO_WEBHOOK_ROUTE = '/api/store/webhooks/mercadopago' as const;

/**
 * The largest notification body this API will read.
 *
 * A webhook is an unauthenticated POST from the internet. 64 KiB is orders of
 * magnitude more than any notification Mercado Pago sends, and small enough that
 * a flood of them cannot exhaust an isolate.
 */
export const STORE_WEBHOOK_MAX_BODY_BYTES = 64 * 1024;

/** The header an order-creation request carries its idempotency key in. */
export const STORE_IDEMPOTENCY_HEADER = 'idempotency-key' as const;

export const STORE_ORDERS_PAGE_SIZE_DEFAULT = 20;
export const STORE_ORDERS_PAGE_SIZE_MAX = 50;

/* -------------------------------------------------------------------------- */
/*  Requests                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Everything a client may say when it wants to buy something.
 *
 * One field. The server reads the price, the currency, the quantity, the
 * duration, the expiry, the reference and the recipient from its own tables.
 */
export const storeCreateOrderRequestSchema = z
  .object({
    productId: z.string().min(3).max(64),
    /**
     * The price the screen was SHOWING. A claim to be checked, never obeyed.
     *
     * The server prices the order from its own clock and refuses with
     * `price_changed` if this disagrees - which is what stops a page left open
     * across the end of the promotion from buying at yesterday's price, and
     * equally stops a player being charged more than the number they were
     * looking at. Omitting it is allowed; sending a different one never buys
     * anything.
     */
    expectedPriceCents: z.number().int().min(1).optional(),
    /** Which wording the player ticked. Stored on the order, never inferred. */
    acceptedTermsVersion: z.string().min(1).max(64).optional(),
  })
  .strict();
export type StoreCreateOrderRequest = z.infer<typeof storeCreateOrderRequestSchema>;

export const storeOrdersQuerySchema = z
  .object({
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(STORE_ORDERS_PAGE_SIZE_MAX).optional(),
  })
  .strict();

/* -------------------------------------------------------------------------- */
/*  The catalogue                                                             */
/* -------------------------------------------------------------------------- */

/**
 * One product as the shop lists it.
 *
 * `priceCents` travels as an integer because centavos fit comfortably in a
 * double; `cashAmount` travels as a decimal STRING because it is a `bigint` in
 * a forty-digit economy and a JSON number would quietly round it.
 */
export const storeCatalogEntryWireSchema = z.object({
  productId: z.string().min(1),
  version: z.number().int().min(1),
  kind: z.enum(STORE_PRODUCT_KINDS),
  name: z.string().min(1),
  description: z.string().min(1),
  /**
   * What this costs RIGHT NOW, decided by the server.
   *
   * The number the charge will use. `normalPriceCents` and `promoPriceCents`
   * are alongside it so a card can strike one through and highlight the other,
   * but the interface never computes this from them - the promotion's state is
   * the server's to decide.
   */
  priceCents: z.number().int().min(1),
  /** The list price. Equal to `priceCents` when no promotion is running. */
  normalPriceCents: z.number().int().min(1),
  /** The promotional price, when this product has one. */
  promoPriceCents: z.number().int().min(1).nullable(),
  /** `R$ 4,90`, derived from the same integer the charge uses. */
  priceLabel: z.string().min(1),
  normalPriceLabel: z.string().min(1),
  /** Retired from sale. Never true on the public catalogue, which omits them. */
  legacy: z.boolean(),
  /** Can this be bought right now? False while the shop is shut. */
  available: z.boolean(),
  currency: z.enum(STORE_CURRENCIES),
  cashAmount: z.string().regex(/^\d+$/),
  vipSeconds: z.number().int().min(0),
  displayOrder: z.number().int().min(0),
  /**
   * What buying this would do to an outstanding Cash debt.
   *
   * Computed by the SERVER, per product, rather than left to the interface to
   * work out from two numbers. It is the same arithmetic the grant performs,
   * so what the card promises and what the transaction does cannot drift.
   * Null for a VIP product: VIP settles nothing.
   */
  debtPreview: z
    .object({
      debtSettled: z.string().regex(/^\d+$/),
      cashCredited: z.string().regex(/^\d+$/),
      debtRemaining: z.string().regex(/^\d+$/),
    })
    .nullable(),
});

/** What is left of the account's rolling limit. Server-computed, always. */
export const storeLimitWireSchema = z.object({
  usedCents: z.number().int().min(0),
  remainingCents: z.number().int().min(0),
  limitCents: z.number().int().min(0),
  pendingOrders: z.number().int().min(0),
  maxPendingOrders: z.number().int().min(0),
  /**
   * When the oldest committed order leaves the window, as an ISO instant.
   *
   * From the SERVER. The interface may count down towards it, but it must
   * never work the moment out for itself: a browser clock that is an hour
   * fast would promise an allowance that does not exist yet. Null when
   * nothing is committed.
   */
  resetsAt: z.string().nullable(),
});
export type StoreLimitWire = z.infer<typeof storeLimitWireSchema>;

export const storeCatalogResponseSchema = z.object({
  contractVersion: z.literal(STORE_CONTRACT_VERSION),
  /**
   * Is the store able to take money at all?
   *
   * False whenever no provider is configured. The interface uses it to say so
   * plainly rather than offering a button that cannot work.
   */
  purchasingEnabled: z.boolean(),
  /** Which shop this is. Bumped when the set of products changes. */
  catalogVersion: z.number().int().min(1),
  /** The wording a purchase must accept. Shown, and stored on the order. */
  termsVersion: z.string().min(1),
  /**
   * Which game this is, decided by SERVER configuration and never inferred.
   *
   * It selects the warning, the terms version and which promotional window (if
   * any) may apply. A browser that could choose it could choose which reset it
   * was being warned about.
   */
  stage: z.enum(RELEASE_STAGES),
  /** The stage's own notice: headline, summary, rules and the checkbox text. */
  notice: z.object({
    headline: z.string().min(1),
    summary: z.string().min(1),
    paragraphs: z.array(z.string().min(1)).min(1),
    commonRules: z.array(z.string().min(1)).min(1),
    wiped: z.array(z.string().min(1)),
    checkbox: z.string().min(1),
    /** False only in `official`, where nothing is scheduled to be erased. */
    resetsAtEnd: z.boolean(),
    /** Prices and packages may still move. Shown in place of "definitive". */
    changesNotice: z.string().min(1),
    vipNote: z.string().min(1),
  }),
  /**
   * The promotion, decided ENTIRELY by the server's clock.
   *
   * `serverNow` is here so an interface can run a countdown against an instant
   * it did not invent: a browser told to believe it is next year must not be
   * able to extend a discount, and one with a slow clock must not lose it
   * early. The countdown is presentation; the price is not.
   */
  promotion: z.object({
    active: z.boolean(),
    serverNow: z.string(),
    startsAt: z.string().nullable(),
    endsAt: z.string().nullable(),
    remainingSeconds: z.number().int().min(0),
    /** 5000 while running, 0 otherwise. Integer basis points, never a float. */
    discountBasisPoints: z.number().int().min(0),
  }),
  currency: z.enum(STORE_CURRENCIES),
  products: z.array(storeCatalogEntryWireSchema),
  limit: storeLimitWireSchema,
  /**
   * How much Cash this account owes back after a refund it could not cover.
   *
   * A decimal string, zero for almost everybody. It is shown because an
   * account under it cannot spend Cash and deserves to know why - and because
   * a purchase clears it, which changes what a package delivers.
   */
  cashDebt: z.string().regex(/^\d+$/),
  /** VIP right now, so the shop can say what a renewal would extend. */
  vip: z
    .object({
      active: z.boolean(),
      endsAt: z.string().nullable(),
      remainingSeconds: z.number().int().min(0),
    })
    .nullable(),
});
export type StoreCatalogResponse = z.infer<typeof storeCatalogResponseSchema>;

/* -------------------------------------------------------------------------- */
/*  Orders                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * What the buyer needs in order to pay.
 *
 * Present only while the charge is payable. Every field here is erased from the
 * database the moment the order reaches a terminal state, so a finished order
 * carries nulls rather than a code somebody could still scan.
 */
export const storePixInstructionsWireSchema = z.object({
  /** The Pix copia-e-cola string. */
  qrCode: z.string().min(1).nullable(),
  /** The same code as an image, base64. */
  qrCodeBase64: z.string().min(1).nullable(),
  /** The provider's hosted payment page, when it gives one. */
  ticketUrl: z.string().url().nullable(),
});
export type StorePixInstructionsWire = z.infer<typeof storePixInstructionsWireSchema>;

/** One order, as its own buyer sees it. Nobody else may fetch it. */
export const storeOrderWireSchema = z.object({
  orderId: z.string().uuid(),
  state: z.enum(STORE_ORDER_STATES),
  /** What was sold, frozen at creation. Survives the catalogue changing. */
  product: storeProductSnapshotSchema,
  amountCents: z.number().int().min(1),
  priceLabel: z.string().min(1),
  currency: z.enum(STORE_CURRENCIES),
  provider: z.string().min(1),
  providerEnvironment: z.string().min(1),
  createdAt: z.string(),
  expiresAt: z.string().nullable(),
  approvedAt: z.string().nullable(),
  grantedAt: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  refundedAt: z.string().nullable(),
  /**
   * The OFFER this order was sold under, frozen at creation.
   *
   * All five are nullable because orders placed before the closed alpha's
   * pricing existed were sold under no promotion and accepted no terms.
   * Inventing values for them would be writing history that did not happen, so
   * the history screen shows what is there and says nothing about what is not.
   *
   * Read from the ORDER, never recomputed: a receipt that re-derives its own
   * discount from today's catalogue stops being a receipt.
   */
  normalPriceCents: z.number().int().min(1).nullable(),
  normalPriceLabel: z.string().min(1).nullable(),
  /** 5000 when it was bought at half price, 0 when it was not, null if unknown. */
  discountBasisPoints: z.number().int().min(0).nullable(),
  gamePhase: z.enum(RELEASE_STAGES).nullable(),
  /** Which wording the buyer accepted. Null for orders placed before terms. */
  acceptedTermsVersion: z.string().min(1).nullable(),
  /**
   * Does this purchase count towards the recredit? Decided by the SERVER.
   *
   * `null` when the order delivered nothing yet, so there is no purchase to
   * judge. The browser never works this out: it is the same `recreditVerdict`
   * the projection uses, applied to the same rows, so a receipt and a total
   * cannot disagree.
   */
  recreditEligible: z.boolean().nullable(),
  /** Null once the order is finished, or when it never had a charge. */
  pix: storePixInstructionsWireSchema.nullable(),
});
export type StoreOrderWire = z.infer<typeof storeOrderWireSchema>;

export const storeOrdersResponseSchema = z.object({
  contractVersion: z.literal(STORE_CONTRACT_VERSION),
  orders: z.array(storeOrderWireSchema),
  nextCursor: z.string().nullable(),
});
export type StoreOrdersResponse = z.infer<typeof storeOrdersResponseSchema>;

export const storeOrderResponseSchema = z.object({
  contractVersion: z.literal(STORE_CONTRACT_VERSION),
  order: storeOrderWireSchema,
  /** Recomputed on every read, so the shop never shows a stale allowance. */
  limit: storeLimitWireSchema,
});
export type StoreOrderResponse = z.infer<typeof storeOrderResponseSchema>;

/* -------------------------------------------------------------------------- */
/*  Entitlement                                                               */
/* -------------------------------------------------------------------------- */

/**
 * What the alpha reset owes this account, and what it does not.
 *
 * Computed from `cash_purchases`, never from a balance. Exposed as a type here
 * so the future account screen and the future reset runner read one shape.
 */
export const restorableCashWireSchema = z.object({
  /** Purchased, not refunded, not yet restored in this reset generation. */
  restorableCash: z.string().regex(/^\d+$/),
  /** Everything ever bought, refunded or not. For a support conversation. */
  purchasedCashTotal: z.string().regex(/^\d+$/),
  /** Reversed by a refund. Never restorable. */
  refundedCash: z.string().regex(/^\d+$/),
  /** Owed back to us because a refund could not be taken out of the balance. */
  cashDebt: z.string().regex(/^\d+$/),
});
export type RestorableCashWire = z.infer<typeof restorableCashWireSchema>;

/* -------------------------------------------------------------------------- */
/*  The launch recredit                                                       */
/* -------------------------------------------------------------------------- */

/**
 * The query the preview accepts: NOTHING.
 *
 * `.strict()` and empty, so `?accountId=...` is a refusal rather than a
 * parameter that is ignored today and honoured after some future refactor. The
 * account is the authenticated session and there is no other way to name one.
 */
export const storeRecreditPreviewQuerySchema = z.object({}).strict();

/** One reason a purchase is out, and how many were. Never WHICH ones. */
export const storeRecreditExclusionWireSchema = z.object({
  reason: z.enum(RECREDIT_EXCLUSION_REASONS),
  count: z.number().int().min(1),
});

/**
 * What the launch would credit, as an estimate that says it is one.
 *
 * Decimal strings for every quantity of Cash, like every balance on this wire:
 * a JSON number is a double to every client and to most tooling in between.
 *
 * It carries no payment id, no provider reference, no order id, no e-mail, no
 * internal account id and nothing about anybody else. The exclusions are
 * categories and counts - enough to explain a number, never enough to
 * reconstruct a transaction.
 */
export const storeRecreditPreviewResponseSchema = z.object({
  contractVersion: z.literal(STORE_CONTRACT_VERSION),
  /** Always `estimate` today. A paid registry will answer something else. */
  status: z.literal(RECREDIT_PREVIEW_STATUS),
  /** The wording this account's purchases were made under. */
  termsVersion: z.string().min(1),
  /** Which game this is. Selects which projection matters next. */
  stage: z.enum(RELEASE_STAGES),
  /** 30. The bonus on top of the purchased Cash, as a whole percentage. */
  bonusPercent: z.number().int().min(0),
  /** The SERVER's instant, so nothing is dated by a browser's clock. */
  serverNow: z.string(),
  /**
   * One entry per reset, always in the same order: open alpha, then official.
   *
   * Every figure arrives resolved. The browser multiplies nothing - the whole
   * point of sending `grantedCash` beside `eligiblePurchasedCash` is that a
   * screen cannot promise a bonus the server would not honour.
   */
  projections: z
    .array(
      z.object({
        destination: z.enum(RELEASE_TRANSITIONS),
        /** 10000 or 13000. Integer basis points, never a float. */
        multiplierBasisPoints: z.number().int().min(0),
        fundedBy: z.array(z.enum(RELEASE_STAGES)).min(1),
        eligiblePurchasedCash: z.string().regex(/^\d+$/),
        grantedCash: z.string().regex(/^\d+$/),
        bonusCash: z.string().regex(/^\d+$/),
        eligiblePurchaseCount: z.number().int().min(0),
        /**
         * Has this reset already credited THIS account?
         *
         * `granted` once the transition ran, with what it actually paid - which
         * may differ from the projection if a purchase was refunded afterwards.
         * The row is the record; the projection is only an estimate.
         */
        status: z.enum(['pending', 'granted']),
        grantedAt: z.string().nullable(),
        grantedCashActual: z.string().regex(/^\d+$/).nullable(),
      }),
    )
    .min(1),
  eligiblePurchaseCount: z.number().int().min(0),
  excludedPurchaseCount: z.number().int().min(0),
  exclusions: z.array(storeRecreditExclusionWireSchema),
  /** The qualifications that make the number not a promise. Server-authored. */
  disclaimers: z.array(z.string().min(1)).min(1),
});
export type StoreRecreditPreviewResponse = z.infer<typeof storeRecreditPreviewResponseSchema>;
